/**
 ******************************************************************************
 * @file    AppService.js
 * @brief   This file is the common place to send AJAX calls.
 ******************************************************************************
 */

import axios from 'axios';
import Swal from 'sweetalert2';
import { CheckResponse, LogError, LogMessage } from '../AppUtil/AppUtil';
import Session from '../Service/Session';
import i18n from '../i18n';

const AppConfig = window.AppConfig;
let sessionExpired = false;
i18n.loadNamespaces('Core');
// uncomment this to run severless locally
// interceptor to parse the responce body
// axios.interceptors.response.use(
//     (res) => {
//         if(res.data && res.data.body && typeof(res.data.body) != 'object'){
//             res.data.body=JSON.parse(res.data.body);
//         }
//        return res;
//     },
//     (err) => {
//        return Promise.reject(err);
//     }
//  );

export default class ApiService {
	/**
	 * This function is used to make GET Request from Application.
	 * @param url
	 * @param isLoggedIn
	 * @return {Promise<*>}
	 * @constructor
	 */
	static async GetData(url, urlKey = 'default', isLoggedIn = true) {
		try {
			const URL = AppConfig.API_URL[urlKey] + url;
			const params = {};
			if (isLoggedIn) {
				params.headers = {
					Authorization: `Bearer ${Session.getAccessToken()}`
				};
			}
			LogMessage(`Making Get Request to URL: - ${URL}`);
			const response = await axios.get(URL, params);
			if (response.data?.statusCode === 401) {
				this.logout('app_message.user_suspended', 'app_message.user_suspended_message');
			} else {
				return CheckResponse(response.data);
			}
		} catch (err) {
			localStorage.setItem('error', err?.message);
			LogError(err.message);
			if (this.checkIfSessionExpired(err.message)) {
				this.logout('app_message.session_expired', 'app_message.session_expired_message');
			}
		}
	}

	/**
	 * This function is used to make POST Request from Application.
	 * @param url
	 * @param data
	 * @param headers
	 * @return {Promise<*>}
	 * @constructor
	 */

	static async PostData(url, data, urlKey = 'default', isLoggedIn = true) {
		try {
			const URL = AppConfig.API_URL[urlKey] + url;
			
			const params = {};
			if (isLoggedIn) {
				params.headers = {
					Authorization: `Bearer ${Session.getAccessToken()}`
				};
			}
			LogMessage(`Making Post Request to url: = ${URL} data ${JSON.stringify(data)}`);
			const response = await axios.post(URL, data, params);
			if (response.data?.statusCode === 401) {
				this.logout('app_message.user_suspended', 'app_message.user_suspended_message');
			} else {
				return typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;
			}
		} catch (err) {
			LogError(err.message);
			console.log(err.message);
			if (this.checkIfSessionExpired(err.message)) {
				this.logout('app_message.session_expired', 'app_message.session_expired_message');
			}
		}
	}

	static async Subscribe(user, data = {}, isLoggedIn = true) {
		try {
			const params = {};

			params.headers = {
				Authorization: `Zoho-oauthtoken 1000.04786949d09b86d9349e24a702638979.238a47bc7f205bbbdacf142b677daab7`,
				'Access-Control-Allow-Origin': '*'
			};

			const res = axios.post(
				`https://campaigns.zoho.com/api/v1.1/json/listsubscribe?resfmt=JSON&listkey=3z37cfdf959101bde84bc997c2d31646bd1a2e1f90f348c10d34218c092086b218&contactinfo=%7BFirst+Name%3A${user.first_name}%2CLast+Name%3A${user.last_name}%2CContact+Email%3A${user.email}%2CPhone%3A${user.phone}%7D`,
				data,
				params
			);

			
		} catch (err) {
			console.error('Error', err.message);
		}
	}

	/**
	 * This function is used to make PATCH Request from Application.
	 * @param url
	 * @param data
	 * @param headers
	 * @return {Promise<*>}
	 * @constructor
	 */
	static async PatchData(url, data, urlKey = 'default', isLoggedIn = true) {
		try {
			const URL = AppConfig.API_URL[urlKey] + url;
			const params = {};
			if (isLoggedIn) {
				params.headers = {
					Authorization: `Bearer ${Session.getAccessToken()}`
				};
			}
			LogMessage(`Making Post Request to url: = ${URL} data ${JSON.stringify(data)}`);
			const response = await axios.patch(URL, data, params);
			if (response.data?.statusCode === 401) {
				this.logout('app_message.user_suspended', 'app_message.user_suspended_message');
			} else {
				return response.data.body;
			}
		} catch (err) {
			LogError(err.message);
			if (this.checkIfSessionExpired(err.message)) {
				this.logout('app_message.session_expired', 'app_message.session_expired_message');
			}
		}
	}

	/**
	 * This function is used to make Delete Request from Application.
	 * @param url
	 * @return {Promise<void>}
	 * @constructor
	 */
	static async DeleteData(url, urlKey = 'default', isLoggedIn = true) {
		try {
			const URL = AppConfig.API_URL[urlKey] + url;
			const params = {};
			if (isLoggedIn) {
				params.headers = {
					Authorization: `Bearer ${Session.getAccessToken()}`
				};
			}
			LogMessage(`Making Delete Request to URL: - ${URL}`);
			const response = await axios.delete(URL, params);
			if (response.data?.statusCode === 401) {
				this.logout('app_message.user_suspended', 'app_message.user_suspended_message');
				return;
			} else {
				return response.data.body;
			}
		} catch (err) {
			LogError(err.message);
			if (this.checkIfSessionExpired(err.message)) {
				this.logout('app_message.session_expired', 'app_message.session_expired_message');
			}
		}
	}

	/**
	 * This function is used to make have Put Request from Application.
	 * @param url
	 * @return {Promise<void>}
	 * @constructor
	 */

	static async PutData(url, data, urlKey = 'default', isLoggedIn = true) {
		try {
			const URL = AppConfig.API_URL[urlKey] + url;
			const params = {};
			if (isLoggedIn) {
				params.headers = {
					Authorization: `Bearer ${Session.getAccessToken()}`
				};
			}
			LogMessage(`Making Post Request to url: = ${URL} data ${JSON.stringify(data)}`);
			const response = await axios.put(URL, data, params);
			if (response.data?.statusCode === 401) {
				this.logout('app_message.user_suspended', 'app_message.user_suspended_message');
			} else {
				return response.data.body;
			}
		} catch (err) {
			LogError(err.message);
			if (this.checkIfSessionExpired(err.message)) {
				this.logout('app_message.session_expired', 'app_message.session_expired_message');
			}
		}
	}

	static logout(title, message) {
		if (sessionExpired) {
			return;
		}

		sessionExpired = true;

		const getTitle = title =>
			i18n.t(title) === 'app_message.session_expired' ? 'Session Expired!' : i18n.t(title);

		const getText = message =>
			i18n.t(message) === 'app_message.session_expired_message'
				? 'Please login again to renew your session.'
				: i18n.t(message);

		Swal.fire({
			title: getTitle(title),
			text: getText(message),
			allowOutsideClick: false,
			icon: 'warning',
			customClass: 'otp_message_success'
		}).then(() => {
			window.location.href = '/logout';
		});
	}

	static checkIfSessionExpired(message) {
		return (
			message.indexOf('401') > -1 ||
			message.indexOf('Unauthorized') > -1 ||
			message.indexOf('403') > -1 ||
			message.indexOf('Network Error') > -1 // this is a temporary fix for when notification url returns 401, apparently it catches only "Network Error" instead of "401"
		);
	}
}
